import { createApiClient } from "@simpro/api-client";
import { AUTH_STORAGE_KEYS, createCustomerAuth, useAuthStore } from "@simpro/auth";
import { createPush } from "@simpro/push";
import { resolveTenantSlug } from "@/features/salon/lib/resolveTenantSlug";
import { env } from "./env";

/**
 * The one API client and the one auth instance for this app.
 *
 * The tenant is resolved from the host once, at startup, and put in the store before anything is
 * fetched: this app is one salon's booking page, and which salon that is comes from the address the
 * customer followed, never from anything they choose. An address that names no salon keeps the one
 * already known, in memory or in storage, rather than wiping it.
 */
function rememberedTenantSlug(): string {
  const inMemory = useAuthStore.getState().tenantSlug;
  if (inMemory) return inMemory;
  try {
    return globalThis.localStorage.getItem(AUTH_STORAGE_KEYS.tenantSlug) ?? "";
  } catch {
    return "";
  }
}

useAuthStore
  .getState()
  .setTenantSlug(
    resolveTenantSlug(
      globalThis.location?.hostname ?? "",
      globalThis.location?.search ?? "",
      rememberedTenantSlug(),
    ),
  );

export const apiClient = createApiClient({
  baseUrl: env.apiBaseUrl,
  getAccessToken: () => useAuthStore.getState().session?.accessToken ?? null,
  getTenantSlug: () => useAuthStore.getState().tenantSlug,
  refresh: () => auth.refreshSession(),
  onUnauthorized: () => auth.signOutLocally(),
});

/**
 * Customers, not staff: a mobile number and a one-time code. There is no login path, because being
 * signed out is the normal state here rather than a redirect.
 */
/**
 * Reminders and updates on the customer's phone, with SMS as the fallback the server keeps (push
 * design phase 6, FR-NT-043). Firebase loads only once a customer turns it on.
 */
export const push = createPush(apiClient, { app: "salon-booking" });

export const auth = createCustomerAuth({
  apiClient,
  // A shared phone stops getting the last customer's reminders when they sign out (FR-NT-068).
  beforeSignOut: () => push.removeThisDevice(),
});
